import React, { useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Chip,
  Typography,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  styled,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { FaPaintBrush, FaWrench } from "react-icons/fa";
import { FiCalendar } from "react-icons/fi";
import { GrTarget } from "react-icons/gr";
import moment from "moment";
import { BsFillDiamondFill } from "react-icons/bs";
import { ReactComponent as NewRenovationIcon } from "../../../../assets/svg/NewRenovationIcon.svg";
import { ReactComponent as NewDesignIcon } from "../../../../assets/svg/NewDesignIcon.svg";
import { ReactComponent as RightArrowIcon } from "../../../../assets/svg/RightArrowIcon.svg";
import { ReactComponent as SmallDhiramIcon } from "../../../../assets/svg/SmallDhiramIcon.svg";
import { milestoneStatusStyleArray } from "../../config";
import { formattedAmountWithoutAed } from "../../../../utils/CommonFunction";
import ConfirmModel from "../../../../components/ConfirmModel";
import UploadProof from "../Milestones/UploadProof";
import { getApiData } from "../../../../utils/APIHelper";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import id from "date-fns/esm/locale/id";
import { Setting } from "../../../../utils/Setting";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  borderBottom: "none",
  textTransform: "capitalize",
}));

export const getStatusDisplayText = (text) => {
  if (typeof text !== "string") return text;
  const displayText = text
    .replace(/[-_]/g, " ")
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
  return displayText;
};

const getTagIcon = (tag) => {
  switch (tag?.toLowerCase()) {
    case "design":
      return <NewDesignIcon />;
    case "installation":
    case "renovation":
      return <NewRenovationIcon />;
    default:
      return <NewRenovationIcon />;
  }
};

const ActivitiesList = ({
  data,
  details,
  getProjectDetails,
  styles,
  isMobile = false,
  isTablet = false,
  onDeleteActivity,
}) => {
  const { id } = useParams();
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [uploadedImages, setUploadedImages] = useState([]);
  const navigate = useNavigate();

  const componentStyles = styles || {};
  const compact = isMobile || isTablet;

  async function startActivity(activity) {
    try {
      const response = await getApiData(
        Setting.endpoints.startMilestone,
        "PUT",
        {
          proposal_id: details?.proposal_id,
          milestone_id: activity?.id,
          status: "ongoing",
        },
      );
      if (response.success) {
        toast.success(response?.message || "Activity started successfully");
        setSelectedActivity(null);
        setIsModalOpen(false);
        if (getProjectDetails) {
          getProjectDetails();
        }
      }
    } catch (error) {
      toast.error(error || "Something went wrong");
    }
  }

  async function submitActivity(activity) {
    try {
      const response = await getApiData(
        `api/project/milestone/${activity?.id}/delivery`,
        "POST",
        {},
      );
      if (response.success) {
        toast.success(response?.message || "Activity submitted successfully");
        setSelectedActivity(null);
        setIsModalOpen(false);
        setUploadedImages([]);
        if (getProjectDetails) {
          getProjectDetails();
        }
      }
    } catch (error) {
      console.log("err===>", error);
      toast.error(error || "Something went wrong");
    }
  }

  const handleConfirm = () => {
    if (selectedActivity?.status === "approved") {
      startActivity(selectedActivity);
    } else {
      submitActivity(selectedActivity);
    }
  };

  const handleOpenModal = (activity) => {
    setSelectedActivity(activity);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedActivity(null);
    setIsModalOpen(false);
    setUploadedImages([]);
  };

  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {data.map((scope, index) => (
          <Accordion
            key={scope.scope_id}
            defaultExpanded
            sx={{
              background: "#fff",
              borderRadius: "12px !important",
              boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
              overflow: "hidden",
              p: "0 !important",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                backgroundColor: "#f9fafb",
                "& .MuiAccordionSummary-content": {
                  alignItems: "center",
                  justifyContent: "space-between",
                },
                padding: "0 1vw",
                margin: "0 !important",
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3 }}>
                <Typography sx={{ fontWeight: 600, fontSize: "16px" }}>
                  {scope.scope_name}
                </Typography>
                <Typography sx={{ fontSize: "14px", color: "#667085" }}>
                  Start:{" "}
                  {moment(scope.activities[0]?.start_date).format(
                    "DD MMM, YYYY",
                  )}{" "}
                  • End:{" "}
                  {moment(
                    scope.activities[scope.activities.length - 1]?.end_date,
                  ).format("DD MMM, YYYY")}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                {/* Schedules can only be edited while the project is in
                    ready_to_start — the same rule the editor itself
                    enforces — so the CTA only shows there. */}
                {details?.status === "ready_to_start" && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(
                        `/project/details/${id}/scopes/${
                          scope.scope_id
                        }/schedule-editor?scopeName=${encodeURIComponent(
                          scope.scope_name,
                        )}`,
                      );
                    }}
                    title="Set activity dates and recalculate billing milestones and payouts"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      backgroundColor: "#4F46E5",
                      color: "#ffffff",
                      padding: "0.5vh 0.7vw",
                      borderRadius: "6px",
                      border: "none",
                      cursor: "pointer",
                      fontSize: "12px",
                      fontWeight: 500,
                    }}
                  >
                    <FiCalendar size={14} />
                    {/* "Edit" once any activity has a date — the same rule
                        the portal uses to name this button. */}
                    {scope.activities?.some((a) => a.start_date || a.end_date)
                      ? "Edit Schedule"
                      : "Create Schedule"}
                  </button>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(
                      `/project/details/${id}/scopes/${
                        scope.scope_id
                      }/activities-details?scopeName=${encodeURIComponent(
                        scope.scope_name,
                      )}&from=schedule`,
                    );
                  }}
                  className="flex items-center gap-2 bg-blue-600 text-white px-3 py-1 rounded-md text-sm hover:bg-blue-700 transition-colors"
                  style={{
                    backgroundColor: "#4D6BF6",
                    color: "#ffffff",
                    padding: "0.5vh 0.7vw",
                    borderRadius: "6px",
                    border: "none",
                    cursor: "pointer",
                    fontSize: "12px",
                    fontWeight: 500,
                  }}
                >
                  View Details
                </button>
                <Chip
                  label={`${scope.activities.length} Activities`}
                  size="small"
                  sx={{
                    backgroundColor: "#e0e7ff",
                    color: "#1e3a8a",
                    fontWeight: 600,
                    fontSize: "12px",
                    marginRight: "1vw",
                  }}
                />
              </Box>
            </AccordionSummary>

            <AccordionDetails
              sx={{ backgroundColor: "#fff", p: 0, margin: "0 !important" }}
            >
              {compact ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.5,
                    p: 2,
                  }}
                >
                  {scope.activities.map((activity) => {
                    const statusBg =
                      milestoneStatusStyleArray.find(
                        (style) => style.status === activity?.status,
                      )?.backgroundColor || "#FFAD5C";
                    const showActionCta =
                      activity?.status === "approved" ||
                      activity?.status === "ongoing";
                    return (
                      <Box
                        key={activity.id}
                        sx={{
                          position: "relative",
                          backgroundColor: "#fff",
                          padding: "16px",
                          borderRadius: "6px",
                          border: "1px solid #e5e7eb",
                          display: "flex",
                          flexDirection: "column",
                          gap: 1.5,
                        }}
                      >
                        {/* {onDeleteActivity && (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              onDeleteActivity(activity);
                            }}
                            sx={{
                              position: "absolute",
                              top: 6,
                              right: 6,
                              width: 28,
                              height: 28,
                              border: "1px solid #FCA5A5",
                              "&:hover": { backgroundColor: "#FEF2F2" },
                            }}
                          >
                            <DeleteOutlineIcon
                              sx={{ fontSize: 16, color: "#DC2626" }}
                            />
                          </IconButton>
                        )} */}
                        {/* Header: image + name + amount (top-right) */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            justifyContent: "space-between",
                            gap: 1.5,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              minWidth: 0,
                              flex: 1,
                            }}
                          >
                            <Box
                              component="img"
                              src={
                                activity?.milestone_image
                                  ? activity?.milestone_image?.path
                                  : "https://renohome.blob.core.windows.net/reno-cms/e56d3d53-e335-425f-990e-16e6b2bbee1b"
                              }
                              alt={activity.milestone_name}
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: "8px",
                                flexShrink: 0,
                                objectFit: "cover",
                              }}
                            />
                            <Box sx={{ minWidth: 0 }}>
                              <Typography
                                onClick={() =>
                                  navigate(
                                    `/project/details/${id}/schedule/details/${activity.id}`,
                                  )
                                }
                                sx={{
                                  fontSize: "14px",
                                  fontWeight: 500,
                                  color: "#0D0D0E",
                                  cursor: "pointer",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {activity.milestone_name}
                              </Typography>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                  mt: 0.25,
                                }}
                              >
                                {getTagIcon(activity.tags)}
                                <Typography
                                  sx={{ fontSize: "12px", color: "#949DAA" }}
                                >
                                  {activity.tags}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                          <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                            <Typography
                              sx={{ fontSize: "12px", color: "#949DAA" }}
                            >
                              Amount
                            </Typography>
                            <Box
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0.3,
                                fontSize: "14px",
                                color: "#0D0D0E",
                                fontWeight: 500,
                              }}
                            >
                              <SmallDhiramIcon />
                              {activity.total_cost
                                ? formattedAmountWithoutAed(activity.total_cost)
                                : "-"}
                            </Box>
                          </Box>
                        </Box>

                        {/* Start & End date in 1 row, 1:1 */}
                        <Box sx={{ display: "flex", gap: 2 }}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              sx={{ fontSize: "12px", color: "#949DAA" }}
                            >
                              Start Date
                            </Typography>
                            <Typography
                              sx={{ fontSize: "14px", color: "#0D0D0E" }}
                            >
                              {activity.start_date
                                ? moment(activity.start_date).format(
                                    "MMM DD, YYYY",
                                  )
                                : "-"}
                            </Typography>
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              sx={{ fontSize: "12px", color: "#949DAA" }}
                            >
                              End Date
                            </Typography>
                            <Typography
                              sx={{ fontSize: "14px", color: "#0D0D0E" }}
                            >
                              {activity.end_date
                                ? moment(activity.end_date).format(
                                    "MMM DD, YYYY",
                                  )
                                : "-"}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Status (inline, not full row) + Dependencies */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 2,
                            flexWrap: "wrap",
                          }}
                        >
                          <Box
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Box
                              sx={{
                                width: "12px",
                                height: "12px",
                                borderRadius: "50%",
                                border: "3px solid rgb(229, 229, 229)",
                                backgroundColor: statusBg,
                              }}
                            />
                            <Typography
                              sx={{ fontSize: "13px", color: "#0D0D0E" }}
                            >
                              {getStatusDisplayText(activity?.status)}
                            </Typography>
                          </Box>
                          {activity.dependencies?.length ? (
                            <Typography
                              sx={{
                                fontSize: "12px",
                                color: "#64748b",
                                textAlign: "right",
                                flex: 1,
                                minWidth: 0,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              Deps:{" "}
                              {activity.dependencies
                                .map((d) => d.name)
                                .join(", ")}
                            </Typography>
                          ) : null}
                        </Box>

                        {/* CTA */}
                        {showActionCta && (
                          <button
                            onClick={() => handleOpenModal(activity)}
                            style={{
                              alignSelf: "stretch",
                              padding: "8px 12px",
                              borderRadius: "8px",
                              background: "#4D6BF6",
                              color: "#fff",
                              border: "none",
                              cursor: "pointer",
                              fontSize: "13px",
                              fontWeight: 500,
                            }}
                          >
                            {activity?.status === "approved"
                              ? "Start Activity"
                              : "Submit Activity"}
                          </button>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                <TableContainer
                  component={Paper}
                  sx={{
                    width: "100%",
                    overflow: "none !important",
                    boxShadow: "none",
                  }}
                >
                  <Table
                    sx={{
                      "& thead": {
                        "& th": {
                          color: "#949DAA",
                          fontWeight: "500",
                        },
                      },
                      "& tbody": {
                        "& tr:hover": {
                          backgroundColor: "#f7f7f7",
                        },
                        "& tr": {
                          borderBottom: "none",
                        },
                      },
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <StyledTableCell>Activity</StyledTableCell>
                        <StyledTableCell>Type</StyledTableCell>
                        <StyledTableCell>Start Date</StyledTableCell>
                        <StyledTableCell>End Date</StyledTableCell>
                        <StyledTableCell sx={{ flex: 0.5, textAlign: "right" }}>
                          <strong
                            style={{
                              display: "flex",
                              flexDirection: "row",
                              alignItems: "center",
                              justifyContent: "flex-end",
                            }}
                          >
                            Amount (<SmallDhiramIcon />)
                          </strong>
                        </StyledTableCell>
                        <StyledTableCell>Status</StyledTableCell>
                        <StyledTableCell>Dependencies</StyledTableCell>
                        <StyledTableCell>Action</StyledTableCell>
                        {/* <StyledTableCell /> */}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {scope.activities.map((activity, idx) => (
                        <TableRow key={activity.id}>
                          <StyledTableCell>
                            <Box display="flex" alignItems="center">
                              <Box
                                component="img"
                                src={
                                  activity?.milestone_image
                                    ? activity?.milestone_image?.path
                                    : "https://renohome.blob.core.windows.net/reno-cms/e56d3d53-e335-425f-990e-16e6b2bbee1b"
                                }
                                alt="Activity Icon"
                                sx={{
                                  width: 45,
                                  height: 45,
                                  borderRadius: "8px",
                                  border: "2px solid white",
                                  boxShadow: "0px 4px 2.67px 0px #00000026",
                                  marginRight: 1,
                                }}
                              />
                              <span
                                style={{
                                  cursor: "pointer",
                                }}
                                onClick={() =>
                                  navigate(
                                    `/project/details/${id}/schedule/details/${activity.id}`,
                                  )
                                }
                              >
                                {activity.milestone_name}
                              </span>
                            </Box>
                          </StyledTableCell>
                          <StyledTableCell>
                            <Box display="flex" alignItems="center" gap="5px">
                              <Box display="flex" alignItems="center">
                                {getTagIcon(activity.tags)}
                              </Box>
                              <Box color="#949DAA">{activity.tags}</Box>
                            </Box>
                          </StyledTableCell>
                          <StyledTableCell>
                            <p
                              style={
                                componentStyles.greyRow || {
                                  color: "#949DAA",
                                  marginBottom: "2px",
                                  fontWeight: "500",
                                }
                              }
                            >
                              {moment(activity.start_date).format(
                                "MMM DD, YYYY",
                              )}
                            </p>
                          </StyledTableCell>
                          <StyledTableCell>
                            <p
                              style={
                                componentStyles.greyRow || {
                                  color: "#949DAA",
                                  marginBottom: "2px",
                                  fontWeight: "500",
                                }
                              }
                            >
                              {moment(activity.end_date).format("MMM DD, YYYY")}
                            </p>
                          </StyledTableCell>
                          <StyledTableCell
                            sx={{ flex: 0.5, textAlign: "right" }}
                          >
                            {activity.total_cost
                              ? `${formattedAmountWithoutAed(
                                  activity.total_cost,
                                )}`
                              : "-"}
                          </StyledTableCell>
                          <StyledTableCell>
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "row",
                                alignItems: "center",
                                gap: "10px",
                              }}
                            >
                              <Box
                                sx={{
                                  width: "15px",
                                  height: "15px",
                                  borderRadius: "50%",
                                  border: "3px solid rgb(229, 229, 229)",
                                  backgroundColor:
                                    milestoneStatusStyleArray.find(
                                      (style) =>
                                        style.status === activity?.status,
                                    )?.backgroundColor || "#FFAD5C",
                                }}
                              />
                              <Box
                                sx={{
                                  fontSize: "14px",
                                  color: "#0D0D0E",
                                }}
                              >
                                {getStatusDisplayText(activity?.status)}
                              </Box>
                            </Box>
                          </StyledTableCell>
                          <StyledTableCell>
                            <Typography
                              sx={{
                                fontSize: "14px",
                                color: "#64748b",
                              }}
                            >
                              {activity.dependencies?.length
                                ? activity.dependencies
                                    .map((d) => d.name)
                                    .join(", ")
                                : "None"}
                            </Typography>
                          </StyledTableCell>
                          <StyledTableCell>
                            {activity?.status === "approved" ||
                            activity?.status === "ongoing" ? (
                              <button
                                onClick={() => handleOpenModal(activity)}
                                style={
                                  componentStyles.milestoneBtn || {
                                    borderRadius: "4px",
                                    background: "#4D6BF6",
                                    padding: ".7vh .7vw",
                                    color: "#fff",
                                    border: "none",
                                    cursor: "pointer",
                                  }
                                }
                              >
                                {activity?.status === "approved"
                                  ? "Start Activity"
                                  : "Submit Activity"}
                              </button>
                            ) : (
                              "-"
                            )}
                          </StyledTableCell>
                          {/* <StyledTableCell
                            sx={{ width: 56, textAlign: "center" }}
                          >
                            {onDeleteActivity && (
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onDeleteActivity(activity);
                                }}
                                sx={{
                                  width: 36,
                                  height: 36,
                                  backgroundColor: "#FEF2F2",
                                  "&:hover": { backgroundColor: "#FEE2E2" },
                                }}
                              >
                                <DeleteOutlineIcon
                                  sx={{ fontSize: 18, color: "#DC2626" }}
                                />
                              </IconButton>
                            )}
                          </StyledTableCell> */}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>

      {selectedActivity &&
        (selectedActivity?.status === "approved" ? (
          <ConfirmModel
            visible={isModalOpen}
            handleClose={handleCloseModal}
            confirmation={handleConfirm}
            message={`Are you sure you want to start ${selectedActivity?.milestone_name}?`}
          />
        ) : (
          <UploadProof
            open={isModalOpen}
            handleClose={handleCloseModal}
            selectedMilestone={selectedActivity}
            handleConfirm={handleConfirm}
            uploadedImages={uploadedImages}
            setUploadedImages={setUploadedImages}
          />
        ))}
    </>
  );
};

export default ActivitiesList;
